// Kiosk login screen.
//
// Two situations:
//   1. No weighbridge locked yet (first-time device setup): we cannot list
//      staff (the kiosk-staff endpoint is scoped by weighbridge), so we show
//      a password form. After login the operator picks + locks a weighbridge.
//   2. Weighbridge locked (everyday flow): show the staff picker, then a PIN
//      pad (staff has a PIN) or a password fallback (no PIN).

import { useCallback, useEffect, useState } from 'react';
import { fetchKioskStaff, NoPinSetError, type KioskStaffSummary } from '../../../api/kiosk';
import { useKioskStore } from '../../../store/kioskStore';
import { KioskPinPad } from './KioskPinPad';
import styles from './kiosk.module.css';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

function PasswordForm({
  heading,
  subheading,
  onSubmit,
  onBack,
}: {
  heading: string;
  subheading?: string;
  onSubmit: (username: string, password: string) => Promise<void>;
  onBack?: () => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      await onSubmit(username.trim(), password);
    } catch {
      setError('Incorrect username or password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.centered}>
      <h2 className={styles.screenTitle}>{heading}</h2>
      {subheading ? <p className={styles.screenSubtitle}>{subheading}</p> : null}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="kiosk-username">
          Username
        </label>
        <input
          id="kiosk-username"
          className={styles.input}
          value={username}
          autoComplete="username"
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="kiosk-password">
          Password
        </label>
        <input
          id="kiosk-password"
          type="password"
          className={styles.input}
          value={password}
          autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
        />
      </div>
      <div className={styles.error} role="alert">
        {error ?? ''}
      </div>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => void submit()}
        disabled={busy || username.length === 0 || password.length === 0}
      >
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      {onBack ? (
        <button type="button" className={styles.linkBtn} onClick={onBack}>
          ← Back
        </button>
      ) : null}
    </div>
  );
}

export function KioskLoginScreen({ weighbridgeEncId }: { weighbridgeEncId: string | null }) {
  const loginWithPin = useKioskStore((s) => s.loginWithPin);
  const loginWithPassword = useKioskStore((s) => s.loginWithPassword);

  const [staff, setStaff] = useState<KioskStaffSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<KioskStaffSummary | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [forcePassword, setForcePassword] = useState(false);

  // Load the staff picker once a weighbridge is locked.
  useEffect(() => {
    if (!weighbridgeEncId) return;
    let cancelled = false;
    setStaff(null);
    setLoadError(null);
    fetchKioskStaff(weighbridgeEncId)
      .then((rows) => {
        if (!cancelled) setStaff(rows);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load staff for this weighbridge.');
      });
    return () => {
      cancelled = true;
    };
  }, [weighbridgeEncId]);

  const submitPin = useCallback(
    async (pin: string) => {
      if (!selected) return;
      setPinError(null);
      setPinBusy(true);
      try {
        await loginWithPin(selected.enc_id, pin);
      } catch (err) {
        if (err instanceof NoPinSetError) {
          setForcePassword(true);
        } else {
          const status = (err as { response?: { status?: number } }).response?.status;
          setPinError(status === 429 ? 'Too many attempts. Locked — try later.' : 'Incorrect PIN.');
        }
      } finally {
        setPinBusy(false);
      }
    },
    [selected, loginWithPin],
  );

  // Case 1 — device not yet configured: password login to begin setup.
  if (!weighbridgeEncId) {
    return (
      <PasswordForm
        heading="Set up this kiosk"
        subheading="Sign in to choose which weighbridge this device controls."
        onSubmit={loginWithPassword}
      />
    );
  }

  // Case 2 — a staff member is selected → PIN pad or password fallback.
  if (selected) {
    if (forcePassword || !selected.has_pin) {
      return (
        <PasswordForm
          heading={selected.display_name}
          subheading="No PIN set yet — sign in with your password. Set up a PIN for faster login."
          onSubmit={loginWithPassword}
          onBack={() => {
            setSelected(null);
            setForcePassword(false);
            setPinError(null);
          }}
        />
      );
    }
    return (
      <div className={styles.centered}>
        <KioskPinPad
          title={selected.display_name}
          onSubmit={(pin) => void submitPin(pin)}
          onBack={() => {
            setSelected(null);
            setPinError(null);
          }}
          error={pinError}
          disabled={pinBusy}
        />
      </div>
    );
  }

  // Case 2 — staff picker.
  return (
    <div className={styles.centered}>
      <h2 className={styles.screenTitle}>Who are you?</h2>
      <p className={styles.screenSubtitle}>Tap your name to sign in.</p>
      {loadError ? <div className={styles.error}>{loadError}</div> : null}
      {staff === null && !loadError ? <p className={styles.muted}>Loading…</p> : null}
      {staff && staff.length === 0 ? (
        <p className={styles.muted}>No active staff for this weighbridge.</p>
      ) : null}
      <div className={styles.cardGrid}>
        {(staff ?? []).map((s) => (
          <button
            key={s.enc_id}
            type="button"
            className={styles.pickCard}
            onClick={() => {
              setSelected(s);
              setPinError(null);
              setForcePassword(false);
            }}
          >
            <span className={styles.avatar}>{initials(s.display_name)}</span>
            <span className={styles.cardName}>{s.display_name}</span>
            <span className={styles.cardMeta}>{s.has_pin ? 'PIN' : 'Password'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
